import { useQuery, useQueryClient } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import type * as PleasedModel from '@/common/api/models.pleased';
import { PAGE_EVENTS } from '@/mixins/logPageEvent';
import { analytics } from '@/common/logs/EventLogger';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import { ref } from 'vue';

/**
 * Shared TanStack Query for fetching open chats
 * Can be used across the application without duplicate API calls
 */
export function useOpenChatsQuery() {
  const shouldFetch = ref(false);
  const queryClient = useQueryClient();

  const {
    data: openChats,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['openChats'],
    queryFn: async () => {
      const response = await PleasedApi.getOpenChats();

      if (!response || response.length === 0) {
        analytics.track({ name: PAGE_EVENTS.MODAL_CONFIRM_RESUMABLE_CHAT_API_NO_DATA_RECEIVED });
        return null;
      }

      return response[0];
    },
    enabled: shouldFetch,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  /**
   * Enable and fetch open chats
   * Returns cached data if available, otherwise fetches
   */
  const fetchAndCheck = async (): Promise<PleasedModel.ActiveOpenChat | null> => {
    // If we already have data, return it immediately
    if (openChats.value !== undefined) {
      return openChats.value;
    }

    // Otherwise, enable fetching and get the data
    shouldFetch.value = true;
    const result = await refetch();
    // TanStack refetch does not throw — without this, init routing continues as if there was no open chat.
    if (result.error) {
      captureEventException(result.error, ErrorTrackingEvent.ChatbotInitPrefetch);
    }
    return result.data ?? null;
  };

  /**
   * Invalidate the open chats query
   * This will mark the cache as stale and trigger a refetch on next access
   */
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['openChats'] });
  };

  return {
    openChats,
    isLoading,
    isError,
    fetchAndCheck,
    invalidate,
  };
}
